import React, { useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Loading from '@/components/ui/loading/Loading';
import { pageTitles } from '@/constants/pageTitles';
import AcceptCompletePage from '@/features/invitations/components/AcceptCompletePage';
import useValidateInvitation from '@/hooks/invitations/useValidateInvitation';

import {
  invitationTokenMissingError,
  isParentsLimitExceededErrorCode,
  parentsLimitExceededError,
} from '../constants';
import { getInvitationToken } from '../invitationToken';

import AcceptConfirmForm from './AcceptConfirmForm';
import styles from './AcceptConfirmPage.module.scss';
import AcceptErrorContent from './AcceptErrorContent';
import ParentsLimitExceededDescription from './ParentsLimitExceededDescription';

const invalidTokenError = ({ title }: { title: string }) => (
  <AcceptErrorContent
    title={title}
    description={
      <>
        <div>認証ができませんでした。</div>
        <div>再度招待メールをお受け取りください。</div>
      </>
    }
  />
);

const tokenMissingError = (
  <AcceptErrorContent
    title={invitationTokenMissingError.title}
    description={invitationTokenMissingError.description}
  />
);

const parentsLimitError = (
  <AcceptErrorContent
    title={parentsLimitExceededError.title}
    description={<ParentsLimitExceededDescription />}
  />
);

const AcceptPage: React.FC = () => {
  const [complete, setComplete] = useState(false);

  return (
    <Routes>
      <Route
        path="/"
        element={<AcceptPageConfirm setComplete={setComplete} />}
      />
      <Route
        path="complete"
        element={<AcceptCompletePage complete={complete} />}
      />
    </Routes>
  );
};

type AcceptPageConfirmProps = {
  setComplete: React.Dispatch<React.SetStateAction<boolean>>;
};

const AcceptPageConfirm: React.FC<AcceptPageConfirmProps> = ({
  setComplete,
}) => {
  const invitationToken = getInvitationToken();

  if (!invitationToken)
    return (
      <SingleCardTemplateAndHelmet>
        {tokenMissingError}
      </SingleCardTemplateAndHelmet>
    );

  return (
    <AcceptPageConfirmContent
      invitationToken={invitationToken}
      setComplete={setComplete}
    />
  );
};

const AcceptPageConfirmContent: React.FC<
  AcceptPageConfirmProps & { invitationToken: string }
> = ({ invitationToken, setComplete }) => {
  const { data, isFetching, error } = useValidateInvitation({
    invitationToken,
  });
  const student = data?.student;

  if (error?.status != null && error.status >= 500 && error.status <= 599)
    return <ErrorPage type="internal_server_error" />;

  const content = (() => {
    if (isFetching)
      return <Loading className={styles.AcceptConfirmPage__Loading} />;
    if (isParentsLimitExceededErrorCode(error?.error_code))
      return parentsLimitError;
    if (error?.status === 403)
      return invalidTokenError({ title: 'URLの有効期限が切れています' });
    if (error || !student)
      return invalidTokenError({ title: '認証URLが不正です' });

    return (
      <AcceptConfirmForm
        student={student}
        invitationToken={invitationToken}
        setComplete={setComplete}
      />
    );
  })();

  return <SingleCardTemplateAndHelmet>{content}</SingleCardTemplateAndHelmet>;
};

const SingleCardTemplateAndHelmet: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <SingleCardTemplate title={pageTitles.invitationStudentConfirm}>
    <Helmet>
      <title>{pageTitles.invitationStudentConfirm}</title>
    </Helmet>
    {children}
  </SingleCardTemplate>
);

export default AcceptPage;
